import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import AuthContext from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import { IMaskInput } from "react-imask";
import ChangePasswordModal from "../components/ChangePasswordModal";
import ChangeEmailModal from "../components/ChangeEmailModal";
import { FaLock, FaEnvelope } from "react-icons/fa";

const MeuPerfil = () => {
  const { addToast } = useContext(ToastContext);
  const { user: authUser, setUser, logout } = useContext(AuthContext);
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: "",
    sobrenome: "",
    telefone: "",
    sexo: "",
    rg: "",
    data_nascimento: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    municipio: "",
    estado: "",
    possui_deficiencia: "",
    raca: "",
  });

  useEffect(() => {
    if (authUser) {
      setFormData({
        nome: authUser.nome || "",
        sobrenome: authUser.sobrenome || "",
        telefone: authUser.telefone || "",
        sexo: authUser.sexo || "",
        rg: authUser.rg || "",
        data_nascimento: authUser.data_nascimento
          ? new Date(authUser.data_nascimento).toISOString().split("T")[0]
          : "",
        cep: authUser.cep || "",
        endereco: authUser.endereco || "",
        numero: authUser.numero || "",
        complemento: authUser.complemento || "",
        bairro: authUser.bairro || "",
        municipio: authUser.municipio || "",
        estado: authUser.estado || "",
        possui_deficiencia:
          authUser.possui_deficiencia === null ||
          authUser.possui_deficiencia === undefined
            ? ""
            : authUser.possui_deficiencia
            ? "Sim"
            : "Não",
        raca:
          authUser.raca && authUser.raca !== "NAO_INFORMADO"
            ? authUser.raca
            : "",
      });
    }
  }, [authUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMaskedInputChange = (value, mask) => {
    const { name } = mask.el.input;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Dispara a busca apenas quando o CEP for preenchido completamente
  const handleCepComplete = (cep) => {
    fetchCep(cep);
  };

  const fetchCep = async (cep) => {
    try {
      // A requisição agora é feita para o nosso backend, que atua como um proxy
      const { data } = await api.get(`/cep/${cep}`);
      setFormData((prev) => ({
        ...prev,
        endereco: data.logradouro,
        bairro: data.bairro,
        municipio: data.localidade,
        estado: data.uf,
      }));
    } catch (error) {
      addToast(error.response?.data?.message || "Erro ao buscar CEP.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        possui_deficiencia: formData.possui_deficiencia === "Sim",
        raca: formData.raca || "NAO_INFORMADO",
      };
      const { data } = await api.put("/users/profile", payload);
      setUser(data); // Atualiza o usuário no contexto de autenticação
      addToast("Perfil atualizado com sucesso!", "success");
      navigate("/editais");
    } catch (error) {
      addToast(
        error.response?.data?.message || "Erro ao atualizar perfil.",
        "error"
      );
    }
  };

  const handleEmailSubmit = async ({
    email_atual,
    novo_email,
    senha,
  }) => {
    if (!novo_email || !senha) {
      addToast("Informe o novo email e a senha.", "error");
      return;
    }
    try {
      const payload = {
        email_atual,
        novo_email,
        senha,
      };
      const { data } = await api.put("/users/email", payload);
      setEmailModalOpen(false);
      addToast(
        data?.message ||
          "Email atualizado! Enviamos um novo link para o endereço informado. Faça login novamente após confirmar.",
        "success"
      );
      logout();
    } catch (error) {
      addToast(
        error.response?.data?.message || "Erro ao atualizar email.",
        "error"
      );
    }
  };

  const handleChangePassword = async (passwordData) => {
    try {
      await api.put("/users/change-password", passwordData);
      addToast("Senha alterada com sucesso!", "success");
      setPasswordModalOpen(false);
    } catch (error) {
      addToast(
        error.response?.data?.message || "Erro ao alterar senha.",
        "error"
      );
    }
  };

  const isFieldEmpty = (value) =>
    value === undefined || value === null || String(value).trim() === "";

  const raceOptions = [
    { value: "Branca", label: "Branca" },
    { value: "Preta", label: "Preta" },
    { value: "Parda", label: "Parda" },
    { value: "Amarela", label: "Amarela" },
    { value: "Indigena", label: "Indígena" },
    { value: "Nao declarada", label: "Prefiro não declarar" },
  ];

  return (
    <>
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onConfirm={handleChangePassword}
      />
      <ChangeEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onConfirm={handleEmailSubmit}
        currentEmail={authUser?.email || ""}
      />
      <PageHeader title="Meu Perfil" />
      <Card>
        {authUser && authUser.perfil_id === 2 ? (
          <div className="form-modern profile-form">
            <section className="form-section">
              <div className="section-header">
                <h3>Dados do Controlador</h3>
                <p>Informações básicas do seu acesso ao sistema.</p>
              </div>
              <div className="form-grid address-grid">
                <div className="form-group">
                  <label>Nome completo</label>
                  <p className="field-value">
                    {formData.nome} {formData.sobrenome}
                  </p>
                </div>
                <div className="form-group">
                  <label>Email institucional</label>
                  <p className="field-value">{authUser.email}</p>
                </div>
                <div className="form-group">
                  <label>Telefone</label>
                  <p className="field-value">{formData.telefone || "—"}</p>
                </div>
              </div>
            </section>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="form-modern profile-form">
            <section className="form-section">
              <div className="section-header">
                <h3>Dados Pessoais</h3>
                <p>Mantenha seu nome, contato e informações básicas atualizadas.</p>
              </div>
              <div className="form-grid">
                <div
                  className={`form-group ${isFieldEmpty(formData.nome) ? "has-empty" : ""}`}
                >
                  <label>Nome</label>
                  <input
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className={isFieldEmpty(formData.nome) ? "field-empty" : ""}
                  />
                  {isFieldEmpty(formData.nome) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${isFieldEmpty(formData.sobrenome) ? "has-empty" : ""}`}
                >
                  <label>Sobrenome</label>
                  <input
                    name="sobrenome"
                    value={formData.sobrenome}
                    onChange={handleInputChange}
                    required
                    className={
                      isFieldEmpty(formData.sobrenome) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.sobrenome) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${isFieldEmpty(formData.telefone) ? "has-empty" : ""}`}
                >
                  <label>Telefone</label>
                  <IMaskInput
                    mask="(00) 00000-0000"
                    value={formData.telefone}
                    onAccept={(value, mask) => handleMaskedInputChange(value, mask)}
                    name="telefone"
                    placeholder="(99) 99999-9999"
                    className={
                      isFieldEmpty(formData.telefone) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.telefone) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${isFieldEmpty(formData.sexo) ? "has-empty" : ""}`}
                >
                  <label>Sexo</label>
                  <select
                    name="sexo"
                    value={formData.sexo}
                    onChange={handleInputChange}
                    className={isFieldEmpty(formData.sexo) ? "field-empty" : ""}
                  >
                    <option value="">Selecione</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Feminino">Feminino</option>
                  </select>
                  {isFieldEmpty(formData.sexo) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${
                    isFieldEmpty(formData.possui_deficiencia) ? "has-empty" : ""
                  }`}
                >
                  <label>Portador de Deficiência</label>
                  <select
                    name="possui_deficiencia"
                    value={formData.possui_deficiencia}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.possui_deficiencia) ? "field-empty" : ""
                    }
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="Sim">Sim</option>
                    <option value="Não">Não</option>
                  </select>
                  {isFieldEmpty(formData.possui_deficiencia) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${isFieldEmpty(formData.raca) ? "has-empty" : ""}`}
                >
                  <label>Raça</label>
                  <select
                    name="raca"
                    value={formData.raca}
                    onChange={handleInputChange}
                    className={isFieldEmpty(formData.raca) ? "field-empty" : ""}
                    required
                  >
                    <option value="">Selecione</option>
                    {raceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {isFieldEmpty(formData.raca) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${isFieldEmpty(formData.rg) ? "has-empty" : ""}`}
                >
                  <label>RG</label>
                  <IMaskInput
                    mask="00.000.000-0"
                    value={formData.rg}
                    onAccept={(value, mask) => handleMaskedInputChange(value, mask)}
                    name="rg"
                    placeholder="99.999.999-9"
                    className={isFieldEmpty(formData.rg) ? "field-empty" : ""}
                  />
                  {isFieldEmpty(formData.rg) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${
                    isFieldEmpty(formData.data_nascimento) ? "has-empty" : ""
                  }`}
                >
                  <label>Data de Nascimento</label>
                  <input
                    name="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.data_nascimento) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.data_nascimento) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
              </div>
            </section>

            <section className="form-section">
              <div className="section-header">
                <h3>Endereço</h3>
                <p>Use seu CEP para preencher automaticamente o endereço.</p>
              </div>
              <div className="form-grid">
                <div
                  className={`form-group ${isFieldEmpty(formData.cep) ? "has-empty" : ""}`}
                >
                  <label>CEP</label>
                  <IMaskInput
                    mask="00000-000"
                    value={formData.cep}
                    onAccept={(value, mask) => handleMaskedInputChange(value, mask)}
                    onComplete={handleCepComplete}
                    name="cep"
                    placeholder="99999-999"
                    className={isFieldEmpty(formData.cep) ? "field-empty" : ""}
                  />
                  {isFieldEmpty(formData.cep) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group full-width ${
                    isFieldEmpty(formData.endereco) ? "has-empty" : ""
                  }`}
                >
                  <label>Endereço</label>
                  <input
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.endereco) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.endereco) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${
                    isFieldEmpty(formData.numero) ? "has-empty" : ""
                  }`}
                >
                  <label>Número</label>
                  <input
                    name="numero"
                    value={formData.numero}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.numero) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.numero) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group full-width ${
                    isFieldEmpty(formData.complemento) ? "has-empty" : ""
                  }`}
                >
                  <label>Complemento</label>
                  <input
                    name="complemento"
                    value={formData.complemento}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.complemento) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.complemento) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${
                    isFieldEmpty(formData.bairro) ? "has-empty" : ""
                  }`}
                >
                  <label>Bairro</label>
                  <input
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.bairro) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.bairro) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${
                    isFieldEmpty(formData.municipio) ? "has-empty" : ""
                  }`}
                >
                  <label>Município</label>
                  <input
                    name="municipio"
                    value={formData.municipio}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.municipio) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.municipio) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
                <div
                  className={`form-group ${
                    isFieldEmpty(formData.estado) ? "has-empty" : ""
                  }`}
                >
                  <label>Estado</label>
                  <input
                    name="estado"
                    value={formData.estado}
                    onChange={handleInputChange}
                    className={
                      isFieldEmpty(formData.estado) ? "field-empty" : ""
                    }
                  />
                  {isFieldEmpty(formData.estado) && (
                    <span className="field-status">Não preenchido</span>
                  )}
                </div>
              </div>
            </section>

            <div className="form-actions sticky-actions">
              <button type="submit" className="btn-modern btn-compact">
                Salvar Alterações
              </button>
            </div>
          </form>
        )}

        <div className="account-actions">
          <div className="action-info">
            <h4>Segurança da conta</h4>
            <p>Gerencie suas credenciais de acesso ao sistema.</p>
          </div>
          <div className="account-actions-buttons">
            <button
              type="button"
              onClick={() => setPasswordModalOpen(true)}
              className="btn-modern btn-compact"
            >
              <FaLock /> Alterar Senha
            </button>
            <button
              type="button"
              onClick={() => setEmailModalOpen(true)}
              className="btn-modern btn-outline btn-compact"
            >
              <FaEnvelope /> Alterar Email
            </button>
          </div>
        </div>
      </Card>
    </>
  );
};

export default MeuPerfil;
